import { AssessmentSelectDto } from '@@types/api.types';
import {
  TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
  TECHNOLOGY_TAB_HISTORY_LIST_CLASS,
} from '@components/pages/technology/components/shared/technology-container-query.constants';
import { Score } from '@components/shared/score.component';
import { Tile } from '@components/shared/tile.component';
import { cn } from '@lib/utils';
import { formatAssessmentDate } from '@utils/assessment.util';
import { MrlSubTab } from '@utils/query-state.util';
import { LayoutDashboard } from 'lucide-react';
import { FC, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

type MrlAssessmentHistoryListProps = {
  activeTab: MrlSubTab;
  assessments: AssessmentSelectDto[];
  featuredAssessmentId: string | null;
  isLoading?: boolean;
  onSelectAssessment: (assessmentId: string | null) => void;
  onTabChange: (tab: MrlSubTab) => void;
  selectedAssessmentId: string | null;
};

export const MrlAssessmentHistoryList: FC<MrlAssessmentHistoryListProps> = (props) => {
  const {
    activeTab,
    assessments,
    featuredAssessmentId,
    isLoading,
    onSelectAssessment,
    onTabChange,
    selectedAssessmentId,
  } = props;

  const { technologyId } = useParams<{ technologyId: string }>();

  const isOverviewActive = activeTab === 'overview';

  const isAssessmentSelected = useMemo(() => {
    if (activeTab !== 'individual') return () => false;
    return (assessmentId: string) => {
      if (selectedAssessmentId === null) {
        return assessmentId === featuredAssessmentId;
      }
      return assessmentId === selectedAssessmentId;
    };
  }, [activeTab, featuredAssessmentId, selectedAssessmentId]);

  const buildOverviewUrl = (): string => {
    return `/technology/${technologyId}?tab=mrl&mrlTab=overview`;
  };

  const buildAssessmentUrl = (assessmentId: string): string => {
    return `/technology/${technologyId}?tab=mrl&mrlTab=individual&mrlAssessmentId=${assessmentId}`;
  };

  return (
    <Tile isLoading={isLoading}>
      <div className="flex flex-col gap-3">
        {/* MRL Overview Link */}
        <Link
          className={cn(
            'flex w-full items-center gap-5 rounded-lg px-3 py-2.5 text-left transition-all duration-200',
            isOverviewActive
              ? 'ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
              : 'bg-[#F5F5F5] text-[#1F1C1A] hover:ring-2 hover:ring-[#3D2EFFCC]',
          )}
          to={buildOverviewUrl()}
        >
          <LayoutDashboard className="size-6 ml-2.5 min-h-10 shrink-0" />
          <span className="text-sm font-medium">MRL Overview</span>
        </Link>

        {/* Assessment History */}
        {assessments.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="font-medium text-gray-700 text-base my-1">MRL History</p>

            <div className={TECHNOLOGY_TAB_HISTORY_LIST_CLASS}>
              {assessments.map((assessment) => (
                <MrlAssessmentHistoryItem
                  assessment={assessment}
                  assessmentUrl={buildAssessmentUrl(assessment.id)}
                  isSelected={isAssessmentSelected(assessment.id)}
                  key={assessment.id}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};

type MrlAssessmentHistoryItemProps = {
  assessment: AssessmentSelectDto;
  assessmentUrl: string;
  isSelected: boolean;
};

const MrlAssessmentHistoryItem: FC<MrlAssessmentHistoryItemProps> = (props) => {
  const { assessment, assessmentUrl, isSelected } = props;

  return (
    <Link
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md relative p-2 text-left transition-all duration-200',
        TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
        isSelected
          ? 'ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
          : 'bg-gray-100 text-gray-600 hover:ring-2 hover:ring-[#3D2EFFCC]',
      )}
      to={assessmentUrl}
    >
      <Score
        score={assessment.score}
        size="medium"
        type="MRL"
        title="MRL"
        subtitle={
          <div className="flex flex-col gap-0.5 text-xs font-light leading-tight text-gray-600">
            <span className="leading-tight">Score</span>
            <span className="text-gray-500 leading-tight">{formatAssessmentDate(assessment)}</span>
          </div>
        }
      />
    </Link>
  );
};
